import React, { useState, useRef } from 'react';
import {
    Box,
    Input,
    List,
    ListItem,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Button,
    Text,
    Flex,
    Icon,
    InputGroup,
    InputLeftElement,
    Portal,
} from '@chakra-ui/react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

const SearchableSelect = ({ options, value, onChange, placeholder = "", label, isDisabled = false, usePortal = true }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const initialFocusRef = useRef();

    const filteredOptions = options.filter((option) =>
        option.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = options.find((opt) => String(opt.value) === String(value));

    const content = (
        <PopoverContent borderRadius="12px" boxShadow="2xl" p={0} border="none" _focus={{ boxShadow: "2xl" }} zIndex={9999}>
            <PopoverBody p={2}>
                <Box mb={2}>
                    <InputGroup size="md">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={FiSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input
                            ref={initialFocusRef}
                            variant="filled"
                            bg="gray.100"
                            _focus={{ bg: "white", borderColor: "blue.500" }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Box>
                <List spacing={0} maxH="250px" overflowY="auto" className="custom-scrollbar">
                    <ListItem
                        px={3}
                        py={2}
                        borderRadius="lg"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => {
                            onChange({ target: { value: '' } });
                            setIsOpen(false);
                            setSearchTerm('');
                        }}
                    >
                        <Text color="gray.400" fontSize="sm">{placeholder || label}</Text>
                    </ListItem>
                    {filteredOptions.map((option) => (
                        <ListItem
                            key={option.value}
                            px={3}
                            py={2.5}
                            borderRadius="lg"
                            cursor="pointer"
                            bg={String(value) === String(option.value) ? 'blue.50' : 'transparent'}
                            color={String(value) === String(option.value) ? 'blue.600' : 'gray.700'}
                            fontWeight={String(value) === String(option.value) ? '600' : 'normal'}
                            transition="all 0.2s"
                            _hover={{ bg: 'blue.50', color: 'blue.600' }}
                            onClick={() => {
                                onChange({ target: { value: option.value } });
                                setIsOpen(false);
                                setSearchTerm('');
                            }}
                        >
                            <Text fontSize="md">{option.label}</Text>
                        </ListItem>
                    ))}
                    {filteredOptions.length === 0 && (
                        <Box py={6} textAlign="center">
                            <Text color="gray.400" fontSize="sm">No matching options found</Text>
                        </Box>
                    )}
                </List>
            </PopoverBody>
        </PopoverContent>
    );

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => !isDisabled && setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            initialFocusRef={initialFocusRef}
            placement="bottom-start"
            matchWidth
        >
            <PopoverTrigger>
                <Button
                    w="100%"
                    justifyContent="space-between"
                    fontWeight="normal"
                    size="lg"
                    bg="gray.50"
                    borderRadius="12px"
                    isDisabled={isDisabled}
                    _hover={!isDisabled ? { bg: 'gray.100' } : {}}
                    rightIcon={<Icon as={FiChevronDown} />}
                    px={4}
                >
                    <Text noOfLines={1} color={selectedOption ? "gray.800" : "gray.500"}>
                        {selectedOption ? selectedOption.label : placeholder || label}
                    </Text>
                </Button>
            </PopoverTrigger>
            {usePortal ? <Portal>{content}</Portal> : content}

            <style>
                {`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #CBD5E0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #A0AEC0;
                }
                `}
            </style>
        </Popover>
    );
};

export default SearchableSelect;
